<template>
  <div>
    <v-radio-group
      :id="id"
      v-model="confirmed"
      data-cy="confirm-field-radio-group"
      :hint="hint"
      :append-icon="tooltip ? 'mdi-information-outline' : null"
      :required="required"
      :rules="rules"
      @click:append="dialog = !dialog"
    >
      <template #label>
        <div :class="{ 'label-font-small': display.xs }">
          {{ label }}
        </div>
      </template>
      <v-radio
        :id="id + 'No'"
        :class="{ 'mt-8': display.xs, 'mt-4': display.sm, 'mt-2': display.mdAndUp }"
        data-cy="confirm-field-radio-false"
        :value="false"
        :label="t('generic.labels.no')"
      />
      <v-radio
        :id="id + 'Yes'"
        data-cy="confirm-field-radio-true"
        :value="true"
        :label="t('generic.labels.yes')"
      />
      <v-dialog
        v-if="tooltip"
        v-model="dialog"
        data-cy="confirm-field-dialog"
        class="mt-0"
        max-width="550px"
      >
        <v-card>
          <v-card-title class="headline" primary-title>
            {{ label }}
          </v-card-title>
          <v-card-text>
            {{ tooltip }}
          </v-card-text>
          <v-card-actions>
            <v-btn data-cy="confirm-field-btn" text @click="dialog = !dialog">
              {{ t('generic.labels.close') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-radio-group>
  </div>
</template>
<script lang="ts">
import { defineComponent, ref, watch } from 'vue';
import { useDisplay } from 'vuetify';
import { useLabel } from '@/composables/useLabel';

type ValidationResult = true | string;
type ValidationRule = (value) => ValidationResult;

export default defineComponent({
  props: {
    value: {
      type: Boolean,
    },
    modelValue: {
      type: Boolean,
    },
    label: {
      type: String,
      required: true,
    },
    hint: {
      type: String,
      default: null,
    },
    tooltip: {
      type: String,
      default: null,
    },
    required: {
      type: Boolean,
      default: false,
    },
    rules: {
      type: Array as () => ValidationRule[],
      default: () => [],
    },
    id: {
      type: String,
      default: 'confirmFieldRadioGroup',
    },
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const confirmed = ref(props.modelValue !== undefined ? props.modelValue : props.value);
    // Reflect upstream changes
    watch(
      () => props.modelValue,
      (val) => {
        if (val !== undefined) confirmed.value = val;
      }
    );
    watch(
      () => props.value,
      (val) => {
        if (props.modelValue === undefined) confirmed.value = val;
      }
    );
    const dialog = ref(false);
    const { t } = useLabel();
    const display = useDisplay();

    watch(confirmed, (val) => {
      emit('input', val);
      emit('update:modelValue', val);
    });

    return {
      confirmed,
      dialog,
      t,
      display,
    };
  },
});
</script>
<style scoped>
.label-font-small {
  font-size: 14px;
}
</style>
